// ───────────────────────────────────────────────────────────────────────────
// Dossier theme tokens — mirror backend/templates/swiss_grid.html :root vars so
// the React editor/preview and the WeasyPrint PDF stay visually identical.
//
// There is a single template now ("Swiss Grid" / grille suisse). When you tweak
// the backend swiss_grid.html :root variables, update the matching token here in
// the same change.
// ───────────────────────────────────────────────────────────────────────────

export const THEMES = {
  swiss_grid: {
    label: 'Swiss Grid',
    description: 'Grille suisse — typographie nette, trigramme XXL, accents azur',

    // Core palette (mirrors swiss_grid.html :root)
    ink: '#0a0a0a',        // trigram, titles, headings
    accent: '#2563EB',     // labels, numbers, chips, badges, availability dot
    text: '#1f2937',       // body text
    muted: '#6b7280',      // secondary text
    faint: '#9ca3af',      // meta / footer
    line: '#111111',       // strong section rules
    hair: '#e5e7eb',       // hairline dividers
    chipBg: '#eef2ff',
    chipText: '#2563EB',
    badgeBg: '#eef2ff',
    badgeText: '#1e3a8a',
    dateBg: '#eff4ff',

    coverBg: '#ffffff',

    // Legacy aliases consumed by shared chrome (editor toolbar buttons, gallery).
    pageBorder: '#0a0a0a',
    headingColor: '#0a0a0a',
    trigramColor: '#0a0a0a',
    posteColor: '#0a0a0a',
    accentBar: '#2563EB',
    switchAccent: '#2563EB',
    activeBorder: '#2563EB',
    sNumBg: '#2563EB',
    sNumColor: '#ffffff',
    sHeadText: '#0a0a0a',
    sHeadLine: '#e5e7eb',
    cpHeaderBorder: '#e5e7eb',
    cpHeaderName: '#0a0a0a',
    cpHeaderRole: '#6b7280',
    cpHeaderMeta: '#9ca3af',
    cpFooterBorder: '#e5e7eb',
    cpFooterMuted: '#9ca3af',
    cpFooterBrand: '#2563EB',
  },

  bloc_editorial: {
    label: 'Bloc éditorial',
    description: 'Style éditorial épuré — initiales serif marine, sections numérotées, chips monospace',

    // Cover renders with the editorial layout (centered serif initials).
    layout: 'editorial',

    // Core palette (mirrors bloc_editorial.html :root)
    ink: '#22395a',        // initials, titles, rules, name
    accent: '#22395a',     // section numbers, language border, société
    text: '#1f2937',
    muted: '#6b7280',
    faint: '#9aa1ab',
    line: '#22395a',
    hair: '#e5e7eb',
    chipBg: '#f3f4f6',
    chipText: '#374151',
    badgeBg: '#f3f4f6',
    badgeText: '#22395a',
    dateBg: '#f3f4f6',

    coverBg: '#ffffff',

    // Legacy aliases consumed by shared chrome (editor toolbar buttons, gallery).
    pageBorder: '#22395a',
    headingColor: '#22395a',
    trigramColor: '#22395a',
    posteColor: '#22395a',
    accentBar: '#22395a',
    switchAccent: '#22395a',
    activeBorder: '#22395a',
    sNumBg: '#22395a',
    sNumColor: '#ffffff',
    sHeadText: '#22395a',
    sHeadLine: '#e5e7eb',
    cpHeaderBorder: '#e5e7eb',
    cpHeaderName: '#22395a',
    cpHeaderRole: '#6b7280',
    cpHeaderMeta: '#9aa1ab',
    cpFooterBorder: '#e5e7eb',
    cpFooterMuted: '#9aa1ab',
    cpFooterBrand: '#22395a',
  },
};

// Default fallback when an unknown template_id is passed
export const DEFAULT_TEMPLATE = 'swiss_grid';

export const getTheme = (templateId) => THEMES[templateId] || THEMES[DEFAULT_TEMPLATE];

// ───────────────────────────────────────────────────────────────────────────
// Structured sections — must stay in sync with backend SECTION_LABELS / order.
// ───────────────────────────────────────────────────────────────────────────
export const DEFAULT_SECTIONS = ['formations', 'competences', 'experiences'];

export const SECTION_LABELS = {
  formations: 'Formation',
  competences: 'Compétences',
  experiences: 'Expériences professionnelles',
};

// ───────────────────────────────────────────────────────────────────────────
// A4 dimensions (96dpi) used by WeasyPrint and the editor canvas
// ───────────────────────────────────────────────────────────────────────────
export const PAGE_W = 794;
export const PAGE_H = 1123;
export const PAGE_PAD_X = 64;
export const PAGE_BORDER_W = 0;

export const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

export const computeTrigram = (name) => {
  if (!name) return '???';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 3).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1].substring(0, 2)).toUpperCase();
};

export const formatRefAndDate = (name, date = new Date()) => {
  const trigram = computeTrigram(name);
  return {
    trigram,
    ref: `${trigram}-${date.getFullYear()}`,
    dateStr: `${MONTHS_FR[date.getMonth()]} ${date.getFullYear()}`,
  };
};

// Render **bold** markdown spans inside a bullet to React nodes.
export const renderInlineBold = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
};

// ───────────────────────────────────────────────────────────────────────────
// Normalize a dossier's structured content into the shapes the editor expects.
// Handles legacy formats (competences as string, missing new fields).
// ───────────────────────────────────────────────────────────────────────────
export const normalizeCompetences = (comp) => {
  if (Array.isArray(comp) && comp.length && typeof comp[0] === 'object' && comp[0] !== null) {
    return comp.map((g) => ({
      domain: g.domain || '',
      tags: Array.isArray(g.tags) ? g.tags.filter(Boolean).map(String) : [],
    }));
  }
  // Legacy string / flat list → best-effort parse into domain groups.
  let lines = [];
  if (typeof comp === 'string') lines = comp.split(/[\n;]+/);
  else if (Array.isArray(comp)) lines = comp.map(String);
  lines = lines.map((l) => l.trim()).filter(Boolean);
  const groups = [];
  lines.forEach((line) => {
    if (line.includes(':')) {
      const [domain, rest] = line.split(/:(.*)/s);
      groups.push({
        domain: domain.trim(),
        tags: rest.split(',').map((t) => t.trim()).filter(Boolean),
      });
    } else if (groups.length) {
      groups[groups.length - 1].tags.push(line);
    } else {
      groups.push({ domain: 'Compétences', tags: [line] });
    }
  });
  return groups;
};

export const normalizeFormations = (formations) =>
  (Array.isArray(formations) ? formations : []).map((f) => ({
    year: f.year || '',
    title: f.title || '',
    level: f.level || '',
    institution: f.institution || '',
  }));

export const normalizeLanguages = (languages) =>
  (Array.isArray(languages) ? languages : []).map((l) => ({
    language: l.language || '',
    level: l.level || '',
    certification: l.certification || '',
    institution: l.institution || '',
    year: l.year || '',
  }));

export const normalizeExperiences = (experiences) =>
  (Array.isArray(experiences) ? experiences : []).map((e) => ({
    duree: e.duree || '',
    poste: e.poste || '',
    seniority: e.seniority || '',
    entreprise: e.entreprise || '',
    mission: e.mission || (Array.isArray(e.bullets) ? e.bullets.join('\n') : ''),
    environnement: e.environnement || '',
  }));
